import { useLayoutEffect } from "react"
import { cn } from "../lib/utils"
import { useFormStore } from "../stores/use-form-store"
import type { FormConfig, FormType } from "../types/form-types"
import { Stage } from "./stage"
import { StageNavigator } from "./stage-navigator"
import { Card, CardContent } from "./ui/card"
import { WidgetStateNavigator } from "./widget-stage-navigator"
import React from "react"
import { logger } from "@/utils/logger"
import {
	APPLICATION_COMPLETED_STATUS,
	FORM_RENDERER_CONTAINER_ID,
} from "@/lib/constants"
import { useLogicStore } from "@/stores/use-conditional-logic-store"

interface FormRenderProps {
	config: FormConfig
	type: FormType
	universityId: string
	batchId: string
	isPreviewMode: boolean
	applicationId: string
	currentStageIdx: number
	applicationStatus: string
	isLeadPunch: boolean
	isReferralWidget: boolean
	source: string
	isRenewal: boolean
}

export const FormRender = React.memo(
	({
		config,
		type = "FORM",
		universityId,
		batchId,
		isPreviewMode = false,
		applicationId,
		currentStageIdx = 0,
		applicationStatus,
		isLeadPunch,
		isReferralWidget,
		source,
		isRenewal,
	}: FormRenderProps) => {
		const { currentStageIndex, setCurrentStageIndex } = useFormStore()
		const {
			setConfig,
			setApplicationId,
			setIsPreview,
			setShouldDisable,
			setIsRenewal,
		} = useFormStore()

		const { hiddenFields } = useLogicStore()

		logger.info("printing-temp: ", currentStageIdx)
		useLayoutEffect(() => {
			setApplicationId(applicationId)
			setConfig({
				...config,
				university_id: universityId,
			})
			// setCurrentStageIndex(currentStageIdx)
			setShouldDisable(
				applicationStatus.toUpperCase() ===
					APPLICATION_COMPLETED_STATUS,
			)
			setIsRenewal(isRenewal)
			setIsPreview(isPreviewMode)
		}, [config, applicationId, isPreviewMode, applicationStatus])

		const alignment = config?.stages_configuration?.alignment || "TOP"
		const isVertical = alignment === "LEFT" || alignment === "RIGHT"
		const layoutConfig = {
			isVertical,
			alignment,
			containerClass: isVertical ? "flex gap-8" : "flex flex-col",
			contentClass: isVertical ? "flex-1 min-w-0" : "w-full",
		}
		const isLastStage = config
			? currentStageIndex === config.stages.length - 1
			: false

		const isFirstStage = currentStageIndex === 0

		const handlePrevious = () => {
			for (let i = currentStageIndex - 1; i >= 0; i--) {
				if (!hiddenFields[config.stages[i].stage_id]) {
					setCurrentStageIndex(i)
					return
				}
			}
		}

		const handleNext = () => {
			// if (currentStageIndex == config.stages.length - 1) return
			// setCurrentStageIndex(currentStageIndex + 1)

			for (let i = currentStageIndex + 1; i < config.stages.length; i++) {
				if (!hiddenFields[config.stages[i].stage_id]) {
					setCurrentStageIndex(i)
					return
				}
			}
		}

		const goToStage = (stageIndex: number) => {
			if (hiddenFields[config.stages[stageIndex].stage_id]) return
			if (stageIndex < 0 || stageIndex >= config.stages.length)
				setCurrentStageIndex(stageIndex)
		}

		return (
			<div
				className="df-form-container h-full w-full"
				id={FORM_RENDERER_CONTAINER_ID}
			>
				<div
					className="df-form-container h-full overflow-auto px-2 py-4"
					style={{ maxWidth: "1200px", margin: "0 auto" }}
				>
					<div
						className={cn(
							layoutConfig.containerClass,
							"w-full @container",
						)}
					>
						{/* Stage Navigator */}
						{type === "FORM" ? (
							config.stages.length > 1 ? (
								<div className="@2xl:block hidden">
									<StageNavigator
										stages={config.stages}
										activeIndex={currentStageIndex}
										config={config.stages_configuration}
										onStageChange={goToStage}
									/>{" "}
								</div>
							) : null
						) : config.stages.length > 1 ? (
							<WidgetStateNavigator
								activeIndex={currentStageIndex}
								stagesCount={config.stages.length}
							/>
						) : null}

						{/* Main Content */}
						<div className={layoutConfig.contentClass}>
							<Card className="rounded-2xl border">
								<CardContent className="px-4 py-4 @2xl:px-8">
									{!hiddenFields[
										config.stages[currentStageIndex]
											?.stage_id
									] && (
										<Stage
											key={
												config.stages[currentStageIndex]
													?.stage_id
											}
											stage={
												config.stages[currentStageIndex]
											}
											isPreview={isPreviewMode}
											isFirstStage={isFirstStage}
											isLastStage={isLastStage}
											applicationId={applicationId}
											universityId={universityId}
											batchId={batchId}
											handleNext={handleNext}
											handlePrevious={handlePrevious}
											config={config}
											isLeadPunch={isLeadPunch}
											isReferralWidget={isReferralWidget}
											source={source}
											isRenewal={isRenewal}
										/>
									)}
								</CardContent>
							</Card>
						</div>
					</div>
				</div>
			</div>
		)
	},
)
